import * as React from 'react';
import { Helmet } from 'react-helmet';
import hljs from 'highlight.js';
import '../../../__core/Themes/HighlightTheme.css';
import { WithTranslation, withTranslation } from 'react-i18next';
import { IMaterial } from '../../../__core/Interfaces/IMaterial';
import { MaterialInHtml } from '../Styles/Materials';
import { copyIcon } from '../Assets/CopyIconHtml';
import ImageZoom from './ImageZoom';
type HtmlMaterialProps = WithTranslation & {
  material: IMaterial;
};

type HtmlMaterialState = {
  zoomedImgSrc: string | null;
};

class HtmlMaterial extends React.Component<
  HtmlMaterialProps,
  HtmlMaterialState
> {
  private _codeBlock = React.createRef<HTMLDivElement>();

  state = {
    zoomedImgSrc: null,
  };

  componentDidMount(): void {
    this.highlight();
    if (typeof window['MathJax'] !== 'undefined') {
      window['MathJax'].typeset();
    }
  }

  componentDidUpdate(prevProps: HtmlMaterialProps): void {
    const { id: materialId } = this.props.material;
    const { id: prevMaterialId } = prevProps.material;

    if (materialId !== prevMaterialId) {
      this.highlight();
      if (typeof window['MathJax'] !== 'undefined') {
        window['MathJax'].typeset();
      }
    }
  }

  addSourceLineNumbersAndCopyButton = (node: Element): void => {
    const { t } = this.props;
    let startLineNumber = 1;
    const result = node.innerHTML.replace(/\n/g, () => {
      startLineNumber++;
      return '\n<div class="line-number">' + startLineNumber + '</div>';
    });

    const lastLineNumberIndex = result.lastIndexOf('<div class="line-number"');
    const htmlWithoutLastLine = result.substring(0, lastLineNumberIndex);
    const { textContent = '' } = node;
    const strings = textContent ? escape(textContent).split('\n') : '';

    node.innerHTML =
      '<div class="line-number">1</div>' +
      htmlWithoutLastLine +
      `<button class="copy-to-clipboard" title="${t('Copy')}" onclick="
        function copy(){
        const contentLines = '${strings}'.split(',')
        const el = document.createElement('textarea');
        el.value = unescape(contentLines.join('\\n'));
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);  
    }; 
    copy()">${copyIcon}</button>`;
  };

  highlight = (): void => {
    if (this._codeBlock.current) {
      const nodes: NodeListOf<HTMLElement> =
        this._codeBlock.current.querySelectorAll('pre code');

      nodes.forEach((node: HTMLElement) => {
        hljs.highlightBlock(node);
        this.addSourceLineNumbersAndCopyButton(node);
      });
    }
  };

  handleOnImgClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLImageElement;
    const { tagName, src } = target;

    if (tagName === 'IMG' && src) {
      this.setState({ zoomedImgSrc: target.src });
    }
  };

  onCloseZoom = (): void => this.setState({ zoomedImgSrc: null });

  render(): React.ReactNode {
    const { material } = this.props;
    const { zoomedImgSrc } = this.state;
    return (
      <>
        <Helmet>
          <script type="text/javascript">
            {`MathJax = { 
                tex: { 
                  macros: { RR: '{\\bf R}' },
                  inlineMath: [['$', '$'], ['\\\\(', '\\\\)']]
                }
              };
            `}
          </script>
          <script
            id="MathJax-script"
            async
            src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js"
          ></script>
        </Helmet>
        <ImageZoom imgSrc={zoomedImgSrc} onCloseZoom={this.onCloseZoom} />
        <MaterialInHtml
          onClick={this.handleOnImgClick}
          id="material-with-html-content"
          ref={this._codeBlock}
          dangerouslySetInnerHTML={{ __html: material.content.html }}
        />
      </>
    );
  }
}

export default withTranslation()(HtmlMaterial);
